class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll(':scope [role="tab"]'));
    this.panels = Array.from(this.querySelectorAll(':scope [role="tabpanel"]'));
    if (this.tabs.length === 0) return;

    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.mediaQuery.addEventListener('change', this.handleMediaChange);

    this.applyInitialState();

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.handleActivation(tab));
      tab.addEventListener('keydown', (event) => this.handleKey(event, index));
    });
  }

  disconnectedCallback() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    }
    this.#listObserver?.disconnect();
  }

  get isAccordionMode() {
    return (
      this.classList.contains('product-tabs--mobile-accordion') &&
      this.mediaQuery &&
      this.mediaQuery.matches
    );
  }

  applyInitialState() {
    this.#syncTabList();

    if (this.isAccordionMode) {
      // Accordion: start closed unless the tab is opted-in via data-mobile-open.
      // Every tab is independently focusable. Skip the open/close animation
      // on first paint — set the resting state directly.
      this.tabs.forEach((tab) => {
        tab.tabIndex = 0;
        const shouldOpen = tab.dataset.mobileOpen === 'true';
        const targetId = tab.getAttribute('aria-controls');
        const panel = this.panels.find((p) => p.id === targetId);
        tab.setAttribute('aria-selected', shouldOpen ? 'true' : 'false');
        tab.toggleAttribute('data-active', shouldOpen);
        if (panel) {
          panel.toggleAttribute('hidden', !shouldOpen);
          panel.style.height = shouldOpen ? 'auto' : '';
        }
      });
    } else {
      // Tabs: roving tabindex, first tab open. Clear any inline height
      // we may have set during a prior accordion session.
      this.tabs.forEach((tab, i) => {
        tab.tabIndex = i === 0 ? 0 : -1;
        this.setExpanded(tab, i === 0);
      });
    }
  }

  handleMediaChange = () => {
    // Re-establish a coherent state when crossing the mobile/desktop boundary.
    // Drop any in-flight tab-switch ease before re-laying out.
    this.#resizeEnd?.();
    this.applyInitialState();
  };

  setExpanded(tab, expanded) {
    tab.setAttribute('aria-selected', expanded ? 'true' : 'false');
    tab.toggleAttribute('data-active', expanded);

    const targetId = tab.getAttribute('aria-controls');
    const panel = this.panels.find((p) => p.id === targetId);
    if (!panel) return;

    if (this.isAccordionMode) {
      // [hidden] toggling lives inside the animation routine because the
      // attribute flip changes which CSS rule applies (height: 0 vs auto)
      // and we have to lock the start height inline first, otherwise the
      // browser snaps to the new state before the transition can run.
      this.animateAccordionPanel(panel, expanded);
    } else {
      panel.toggleAttribute('hidden', !expanded);
      // Tabs mode: clear any leftover inline height from a prior accordion
      // session (e.g. resize from mobile → desktop while a panel was open).
      panel.style.height = '';
    }
  }

  // Pixel-height open/close animation. Measure inner.scrollHeight, lock
  // the panel's height inline before flipping [hidden], then transition
  // to the measured target. On open, transitionend clears the inline
  // height so future content changes (e.g. nested spec-group accordions)
  // can reflow the panel naturally.
  animateAccordionPanel(panel, expanded) {
    const inner = panel.firstElementChild;
    if (!inner) return;

    // Cancel any in-flight transition end listener from a prior toggle —
    // a fast tap-tap could otherwise land us back at `auto` mid-close.
    if (panel.__accordionEndHandler) {
      panel.removeEventListener('transitionend', panel.__accordionEndHandler);
      panel.__accordionEndHandler = null;
    }

    // Read the current rendered height BEFORE any attribute or style
    // change — this is our transition's `from` value.
    const startHeight = panel.getBoundingClientRect().height;

    if (expanded) {
      // OPEN. Lock the start height inline first so removing [hidden]
      // doesn't let CSS snap to height: auto.
      panel.style.height = startHeight + 'px';
      panel.removeAttribute('hidden');

      // Inner is now laid out (parent's overflow: hidden doesn't affect
      // child layout); scrollHeight reflects its natural full size.
      const targetHeight = inner.scrollHeight;

      // Reflow with the locked start, then move to target — gives the
      // browser two distinct frame values to interpolate between.
      void panel.offsetHeight;
      panel.style.height = targetHeight + 'px';

      const onEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'height') return;
        panel.style.height = '';
        panel.removeEventListener('transitionend', onEnd);
        panel.__accordionEndHandler = null;
      };
      panel.__accordionEndHandler = onEnd;
      panel.addEventListener('transitionend', onEnd);
    } else {
      // CLOSE. Lock current height inline, reflow, then animate to 0.
      // [hidden] is set AFTER the inline `0px` so its `display: block` +
      // base `height: 0` rule matches the inline value — no snap.
      panel.style.height = startHeight + 'px';
      void panel.offsetHeight;
      panel.style.height = '0px';
      panel.setAttribute('hidden', '');
    }
  }

  handleActivation(tab) {
    if (this.isAccordionMode) {
      // Toggle this row independently — multiple panels can be open at once,
      // and clicking an already-open row closes it.
      const isOpen = tab.hasAttribute('data-active');
      this.setExpanded(tab, !isOpen);
    } else {
      const previousHeight = this.offsetHeight;

      // Tabs: activate this one, deactivate all others.
      this.tabs.forEach((t) => {
        const isActive = t === tab;
        this.setExpanded(t, isActive);
        t.tabIndex = isActive ? 0 : -1;
      });

      this.#easeResize(previousHeight);
      this.#scrollTabIntoView(tab);
    }
  }

  // Frelan: in tabs mode the tab buttons live in one `.product-tabs__list` row
  // (role="tablist") instead of loose in the flex-wrap container, where they spilled
  // onto a second line as the column narrowed. The row never wraps: it spreads across
  // the width when there's room and scrolls sideways (hidden scrollbar, faded edge,
  // active tab kept in view) when there isn't. Each tab block renders its button and
  // panel side by side with no wrapper, so the list is assembled here; the mobile
  // accordion needs every button back directly above its own panel, so it's taken
  // apart again there.
  #list = null;
  #listObserver = null;

  #syncTabList() {
    if (this.isAccordionMode) {
      if (!this.#list) return;
      this.tabs.forEach((tab) => {
        const panel = this.panels.find((p) => p.id === tab.getAttribute('aria-controls'));
        if (panel) panel.before(tab);
      });
      this.#listObserver?.disconnect();
      this.#list.remove();
      this.#list = null;
      return;
    }

    if (!this.#list) {
      const list = document.createElement('div');
      list.className = 'product-tabs__list';
      list.setAttribute('role', 'tablist');
      list.addEventListener('scroll', () => this.#updateListOverflow(), { passive: true });
      this.prepend(list);
      this.#list = list;
      this.#listObserver = new ResizeObserver(() => this.#updateListOverflow());
      this.#listObserver.observe(list);
    }

    this.#list.append(...this.tabs);
    this.#updateListOverflow();
  }

  // Flags which edges have hidden tabs beyond them, for the CSS edge fades.
  #updateListOverflow() {
    const list = this.#list;
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    list.toggleAttribute('data-overflow-start', list.scrollLeft > 1);
    list.toggleAttribute('data-overflow-end', max - list.scrollLeft > 1);
  }

  // Scrolls only the tab row (never the page or the details column).
  #scrollTabIntoView(tab) {
    const list = this.#list;
    if (!list || list.scrollWidth <= list.clientWidth) return;

    const fade = 32;
    // The list is position: relative, so offsetLeft is measured from its start.
    const left = tab.offsetLeft;
    const right = left + tab.offsetWidth;
    let target = list.scrollLeft;
    if (left - fade < list.scrollLeft) target = left - fade;
    else if (right + fade > list.scrollLeft + list.clientWidth) target = right + fade - list.clientWidth;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({ left: Math.max(0, target), behavior: reduce ? 'auto' : 'smooth' });
  }

  // Frelan: ease the block between the old and new panel heights instead of snapping.
  //   Shrinking (Description → Standards): hold a min-height at the old height and ease it
  //   down, so the space closes smoothly — everything below (add-on products) glides up,
  //   and a clamped scroll in the desktop details column glides rather than jumps.
  //   Growing (Standards → Description): pin the height at the old value with overflow
  //   clipped and ease it open, so the new panel is revealed downwards and the content
  //   below is pushed down smoothly.
  // Reduced motion: straight to the new height.
  #resizeEnd = null;

  #easeResize(previousHeight) {
    this.#resizeEnd?.();

    const nextHeight = this.offsetHeight;
    if (nextHeight === previousHeight) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const growing = nextHeight > previousHeight;
    const property = growing ? 'height' : 'min-height';
    const easing = 'var(--animation-easing, ease)';

    this.style.transition = 'none';
    if (growing) this.style.overflow = 'clip';
    this.style.setProperty(property, `${previousHeight}px`);
    void this.offsetHeight;
    this.style.transition = `${property} 0.3s ${easing}`;
    this.style.setProperty(property, `${nextHeight}px`);

    const end = () => {
      clearTimeout(fallback);
      this.removeEventListener('transitionend', onEnd);
      this.style.transition = '';
      this.style.removeProperty(property);
      this.style.overflow = '';
      this.#resizeEnd = null;
    };
    const onEnd = (event) => {
      if (event.target === this && event.propertyName === property) end();
    };
    // transitionend doesn't fire in a background tab or if the block is hidden mid-way.
    const fallback = setTimeout(end, 450);

    this.addEventListener('transitionend', onEnd);
    this.#resizeEnd = end;
  }

  handleKey(event, index) {
    const last = this.tabs.length - 1;
    let next = -1;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.handleActivation(this.tabs[index]);
        return;
    }

    if (next >= 0) {
      event.preventDefault();
      if (!this.isAccordionMode) {
        this.handleActivation(this.tabs[next]);
      }
      this.tabs[next].focus();
    }
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
